import {useMemo, useState} from "react";
import {App as AntdApp, Table, AutoComplete, Popconfirm, Empty, Alert} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetTrackedKeywordsQuery,
	useAddTrackedKeywordMutation,
	useDeleteTrackedKeywordMutation,
} from "~/reduxs/api/gscApiSlice";
import RankChart from "./RankChart";
import style from "../style/Gsc.module.scss";

const cn = className.bind(style);

/** Ô thay đổi vị trí: delta = kỳ trước − kỳ này (dương = tăng hạng/cải thiện → xanh). */
function DeltaCell({value}) {
	if (value === null || value === undefined) return <span style={{color: '#9ca3af'}}>—</span>;
	if (value === 0) return <span style={{color: '#9ca3af'}}>0</span>;
	const up = value > 0;
	return (
		<span style={{color: up ? '#16a34a' : '#dc2626', fontWeight: 600}}>
			<FontAwesomeIcon icon={up ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'} /> {Math.abs(value).toFixed(1)}
		</span>
	);
}

const posText = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(1));

/**
 * Tab "Theo dõi thứ hạng": người dùng ghim N từ khoá → xem vị trí trung bình (từ GSC) theo
 * ngày + thay đổi so kỳ trước. Vị trí GSC là TRUNG BÌNH, không phải SERP tức thời. Gate cờ
 * gói can_keyword_tracking (tab chỉ hiện khi gói cho phép — xem GscReports).
 *
 * @param suggestions  danh sách từ khoá gợi ý (top query đã tải) cho ô nhập.
 */
function GscRankTracking({projectId, from, to, suggestions = []}) {

	const {notification} = AntdApp.useApp();
	const {data, isFetching} = useGetTrackedKeywordsQuery({projectId, from, to});
	const [addKeyword, {isLoading: adding}] = useAddTrackedKeywordMutation();
	const [deleteKeyword] = useDeleteTrackedKeywordMutation();

	const [input, setInput] = useState('');
	const [selectedId, setSelectedId] = useState(null);

	const keywords = useMemo(() => data?.keywords || [], [data]);
	const max = data?.max || 50;

	// Từ khoá đang chọn để vẽ biểu đồ (mặc định dòng đầu).
	const selected = useMemo(() => {
		if (!keywords.length) return null;
		return keywords.find((k) => k.id === selectedId) || keywords[0];
	}, [keywords, selectedId]);

	// Gợi ý = top query CHƯA được theo dõi.
	const options = useMemo(() => {
		const tracked = new Set(keywords.map((k) => k.keyword));
		return (suggestions || [])
			.filter((s) => s && !tracked.has(s))
			.slice(0, 50)
			.map((s) => ({value: s}));
	}, [suggestions, keywords]);

	const handleAdd = async () => {
		const kw = input.trim();
		if (!kw) return;
		try {
			const res = await addKeyword({projectId, keyword: kw}).unwrap();
			if (res?.added > 0) {
				notification.success({message: 'Đã thêm', description: `Đang theo dõi "${kw}".`});
			} else {
				notification.info({message: 'Đã có', description: 'Từ khoá này đã trong danh sách theo dõi.'});
			}
			setInput('');
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thêm được từ khoá')});
		}
	};

	const handleDelete = async (row) => {
		try {
			await deleteKeyword({projectId, id: row.id}).unwrap();
			notification.success({message: 'Đã bỏ theo dõi', description: row.keyword});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xoá được')});
		}
	};

	const columns = [
		{title: 'Từ khoá', dataIndex: 'keyword', key: 'keyword', ellipsis: true,
			render: (v, r) => <span style={{fontWeight: r.id === selected?.id ? 700 : 400}}>{v}</span>},
		{title: 'Vị trí', dataIndex: 'position', key: 'position', align: 'center', width: 90,
			render: (v) => <strong>{posText(v)}</strong>},
		{title: 'Thay đổi', dataIndex: 'delta', key: 'delta', align: 'center', width: 100,
			render: (v) => <DeltaCell value={v} />},
		// Cột phụ — ẩn dưới 768px cho gọn màn nhỏ.
		{title: 'Tốt nhất', dataIndex: 'best_position', key: 'best', align: 'center', width: 90,
			responsive: ['md'], render: (v) => posText(v)},
		{title: 'Clicks', dataIndex: 'clicks', key: 'clicks', align: 'center', width: 90},
		{title: 'Hiển thị', dataIndex: 'impressions', key: 'impr', align: 'center', width: 100,
			responsive: ['md']},
		{
			title: '', key: 'action', align: 'right', width: 50,
			render: (_, r) => (
				<Popconfirm title="Bỏ theo dõi từ khoá này?" okText="Bỏ theo dõi" cancelText="Huỷ"
					onConfirm={(e) => { e?.stopPropagation?.(); handleDelete(r); }}
					onCancel={(e) => e?.stopPropagation?.()}>
					<Button small red onClick={(e) => e.stopPropagation()}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</Popconfirm>
			),
		},
	];

	return (
		<div>
			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Vị trí lấy từ Google Search Console — là vị trí TRUNG BÌNH (không phải thứ hạng tức thời), chỉ có với từ khoá site đã từng hiển thị. Nhỏ hơn = tốt hơn." />

			<div className={cn('rangeBar')} style={{marginBottom: 16}}>
				<AutoComplete
					style={{width: 320, maxWidth: '100%'}}
					options={options}
					value={input}
					onChange={setInput}
					onSelect={(v) => setInput(v)}
					filterOption={(inputValue, option) =>
						option.value.toLowerCase().includes(inputValue.toLowerCase())}
					placeholder="Nhập từ khoá cần theo dõi…"
				/>
				<Button primary loading={adding} onClick={handleAdd}
					leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>Thêm</Button>
				<span style={{color: '#9ca3af', fontSize: 12}}>{keywords.length}/{max} từ khoá</span>
			</div>

			{keywords.length === 0 ? (
				<Empty description="Chưa theo dõi từ khoá nào. Thêm từ khoá bạn quan tâm để xem biến động thứ hạng." />
			) : (
				<>
					<Table
						rowKey="id"
						size="small"
						columns={columns}
						dataSource={keywords}
						loading={isFetching}
						scroll={{x: 'max-content'}}
						pagination={{pageSize: 10, hideOnSinglePage: true}}
						onRow={(r) => ({onClick: () => setSelectedId(r.id), style: {cursor: 'pointer'}})}
						rowClassName={(r) => (r.id === selected?.id ? cn('rowActive') : '')}
					/>

					{selected && (
						<div className={cn('section')} style={{marginTop: 8}}>
							<h3 className={cn('sectionTitle')}>Diễn biến vị trí: “{selected.keyword}”</h3>
							<RankChart series={selected.series || []} />
						</div>
					)}
				</>
			)}
		</div>
	);
}

export default GscRankTracking;
